import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Card, Form, Button, Row, Col, Modal } from "react-bootstrap";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import FeaturesIcon from "@mui/icons-material/Extension";
import SearchIcon from "@mui/icons-material/Search";
import BadgeIcon from "@mui/icons-material/Badge";
import Select from "react-select";
import Multiselect from "multiselect-react-dropdown";
import { FormSelect } from "components/elements/form-select/FormSelect";
import { createUser, getEmployeeByIdAPI } from "ServerAPI/serverAPI";
import { roleFeatureMapping, roleIdWithName } from "constants/Constants";
import { useNavigate } from "react-router-dom";
import { useToast } from "context/toast-context/ToastProvider";
import _ from "lodash";
import BulkUserUpload from "./BulkUserUpload";
import { useResponsive } from "components/ResponsiveDesign/ResponsiveDesign";

const AddUserModal = ({
  fetchUsers,
  open,
  handleClose,
  departments,
  organizationDetails,
  usersWithoutRole,
  cookies,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const [userType, setUserType] = useState("normal");
  const [userToggle, setUserToggle] = useState("singleUser");
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    roleId: "",
    phoneNumber: "",
    department: "",
    user_id: "",
  });

  const [searchId, setSearchId] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [error, setError] = useState("");
  const [validated, setValidated] = useState(true);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [features, setFeatures] = useState([]);
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);

  const navigate = useNavigate();
  const { showToast } = useToast();

  const activeTabStyle = useMemo(
    () => ({
      backgroundColor: "#224D99",
      color: "white",
      fontWeight: "bold",
      border: "2px solid #224D99",
      padding: isMobile ? "8px 16px" : "10px 20px",
    }),
    [isMobile]
  );

  const inactiveTabStyle = useMemo(
    () => ({
      backgroundColor: "transparent",
      color: "#224D99",
      fontWeight: "500",
      border: "2px solid #e0e0e0",
      padding: isMobile ? "8px 16px" : "10px 20px",
    }),
    [isMobile]
  );

  useEffect(() => {
    const allFeatures = organizationDetails?.features?.map((feature) => ({
      value: feature?.featureId,
      label: feature?.featureName,
    }));
    setFeatures(allFeatures || []);
    setUserType(cookies.userManagement === "HRMS" ? "guest" : "normal");
  }, [organizationDetails, cookies.userManagement]);

  const filterFeaturesByRole = (selectedRoleId, allFeatures) => {
    if (!selectedRoleId || !allFeatures) return [];
    const allowedFeatures = roleFeatureMapping[selectedRoleId] || [];
    return allFeatures.filter((feature) =>
      allowedFeatures.includes(feature.label)
    );
  };

  useEffect(() => {
    const selectedRole = formData?.roleId;
    const filtered = filterFeaturesByRole(selectedRole, features);
    setFilteredFeatures(filtered);

    if (selectedFeatures && selectedFeatures.length > 0) {
      const validFeatures = selectedFeatures.filter((selectedFeature) =>
        filtered.some(
          (availableFeature) => availableFeature.value === selectedFeature.value
        )
      );
      if (validFeatures.length !== selectedFeatures.length) {
        setSelectedFeatures(validFeatures);
      }
    }
  }, [formData?.roleId, features]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSearch = async () => {
    if (!searchId.trim()) {
      setSearchError("Please enter an Employee ID");
      return;
    }

    setSearchLoading(true);
    setSearchError("");

    try {
      const response = await getEmployeeByIdAPI(searchId, cookies?.token);

      if (response.status === 200 && response.data.status === "success") {
        const employeeData = response.data.data;
        const nameParts = employeeData.name.split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        setFormData({
          ...formData,
          user_id: employeeData.invensis_id,
          name: firstName,
          lastName: lastName,
          email: employeeData.email,
        });

        setSearchError("");
      } else {
        setSearchError("Employee not found");
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      setSearchError("Failed to fetch employee data. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(false);
    } else {
      setSubmitLoader(true);
      setError("");

      try {
        let payload;

        if (userType === "guest") {
          payload = {
            userManagementId: cookies.userManagementId,
            userId: formData.user_id,
            roleId: formData.roleId,
            organizationId: `${cookies.organizationId}`,
            domain: "",
            department: formData.department || "IT",
            authorUserId: cookies.userId,
            featureIds: selectedFeatures.map((feature) => feature?.value),
            password: "admin",
          };
        } else {
          payload = {
            name: formData.name,
            lastName: formData?.lastName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            roleId: formData.roleId,
            organizationId: `${cookies.organizationId}`,
            domain: "",
            department: formData.department || "IT",
            authorUserId: cookies.userId,
            featureIds: selectedFeatures.map((feature) => feature?.value),
            password: organizationDetails?.userManagementDetails?.ssoMode
              ? false
              : "admin",
          };
        }

        const response = await createUser(payload);

        setFormData({
          name: "",
          lastName: "",
          email: "",
          roleId: "",
          phoneNumber: "",
          department: "",
          user_id: "",
        });
        setSelectedFeatures([]);
        setSearchId("");

        if (
          response?.data?.message !== "User already exists with same email id" &&
          response?.data?.message !== "User already exists with same userId"
        ) {
          showToast("User created successfully", "success");
          fetchUsers();
          handleClose();
          navigate("/about-organization");
        } else {
          showToast(response?.data?.message, "error");
        }
      } catch (err) {
        console.error("Error creating user:", err);
        showToast(err.response?.data?.message, "error");
        setError(err.response?.data?.message || "An unexpected error occurred");
      } finally {
        setSubmitLoader(false);
      }
      setValidated(true);
    }
    setIsSubmitButtonClicked(false);
  };

  const getFormFields = () => {
    const baseFields = [
      {
        label: "Department",
        type: "dropdown",
        name: "department",
        icon: <BusinessIcon style={{ color: "#224D99" }} />,
        options: departments.map((dept) => ({
          value: dept?.title,
          label: dept?.title,
        })),
      },
      {
        label: "Role",
        type: "dropdown",
        name: "roleId",
        icon: <WorkIcon style={{ color: "#224D99" }} />,
        options: _.compact(
          roleIdWithName.map((role) => {
            if (role.id !== cookies.roleId && role.id !== "platformCoordinator") {
              return {
                value: role.id,
                label: role.name,
              };
            }
          })
        ),
      },
      {
        label: "Features",
        type: "multiselect",
        name: "features",
        icon: <FeaturesIcon style={{ color: "#224D99" }} />,
        options: filteredFeatures,
      },
    ];

    if (userType === "guest") {
      return [
        {
          label: "First Name",
          type: "text",
          name: "name",
          disabled: true,
          icon: <PersonIcon style={{ color: "#224D99" }} />,
        },
        {
          label: "Last Name",
          type: "text",
          name: "lastName",
          disabled: true,
          icon: <PersonIcon style={{ color: "#224D99" }} />,
        },
        {
          label: "Employee ID",
          type: "text",
          name: "user_id",
          disabled: true,
          icon: <BadgeIcon style={{ color: "#224D99" }} />,
        },
        {
          label: "Email",
          type: "email",
          name: "email",
          disabled: true,
          icon: <EmailIcon style={{ color: "#224D99" }} />,
        },
        ...baseFields,
      ];
    } else {
      return [
        {
          label: "First Name",
          type: "text",
          name: "name",
          icon: <PersonIcon style={{ color: "#224D99" }} />,
        },
        {
          label: "Last Name",
          type: "text",
          name: "lastName",
          icon: <PersonIcon style={{ color: "#224D99" }} />,
        },
        {
          label: "Email",
          type: "email",
          name: "email",
          icon: <EmailIcon style={{ color: "#224D99" }} />,
        },
        {
          label: "Phone Number",
          type: "text",
          name: "phoneNumber",
          icon: <PhoneIcon style={{ color: "#224D99" }} />,
        },
        ...baseFields,
      ];
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          maxWidth: isMobile ? "95%" : isTablet ? "80%" : "md",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#224D99",
          fontFamily: "Inter, sans-serif",
          fontWeight: "600",
          borderBottom: "2px solid #f0f0f0",
          mb: 2,
          pb: 2,
        }}
      >
        Add User Details
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            "&:hover": {
              backgroundColor: "rgba(34, 75, 153, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {userType === "normal" && (
          <div className="d-flex justify-content-center mb-4">
            <div className="btn-group" role="group">
              <button
                type="button"
                className="btn"
                style={userToggle === "singleUser" ? activeTabStyle : inactiveTabStyle}
                onClick={() => setUserToggle("singleUser")}
              >
                Single User
              </button>
              <button
                type="button"
                className="btn"
                style={userToggle === "bulkUpload" ? activeTabStyle : inactiveTabStyle}
                onClick={() => setUserToggle("bulkUpload")}
              >
                Bulk Upload
              </button>
            </div>
          </div>
        )}

        {userToggle === "singleUser" ? (
          <>
            {userType === "guest" && (
              <Card className="mb-4" style={{ border: "2px solid #e3f2fd" }}>
                <Card.Header style={{ backgroundColor: "#e3f2fd", color: "#224D99" }}>
                  <strong>Search Employee</strong>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-2">
                      <SearchIcon style={{ color: "#224D99" }} />
                      Employee ID
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder="Enter Employee ID"
                        value={searchId}
                        onChange={(e) => setSearchId(e.target.value)}
                        disabled={searchLoading}
                        style={{
                          borderColor: searchError ? "#dc3545" : "#224D99",
                          focusBorderColor: "#224D99",
                        }}
                      />
                      <Button
                        variant="primary"
                        onClick={handleSearch}
                        disabled={searchLoading}
                        style={{
                          backgroundColor: "#224D99",
                          borderColor: "#224D99",
                          minWidth: isMobile ? "50px" : "60px",
                        }}
                      >
                        {searchLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SearchIcon />
                        )}
                      </Button>
                    </div>
                    {searchError && (
                      <Form.Text className="text-danger">{searchError}</Form.Text>
                    )}
                  </Form.Group>
                </Card.Body>
              </Card>
            )}

            <Form onSubmit={handleSubmit}>
              <Card style={{ border: "2px solid #f0f0f0" }}>
                <Card.Body>
                  <Row className="gx-3">
                    {getFormFields().map((field, index) => (
                      <Form.Group
                        as={Col}
                        xs={12}
                        md={field.name === "features" ? 12 : 6}
                        controlId={field.name}
                        key={index}
                        className="mb-3"
                      >
                        <Form.Label className="d-flex align-items-center gap-2 fw-bold">
                          {field.icon}
                          {field.label}
                        </Form.Label>

                        {field.type === "dropdown" ? (
                          <>
                            <Form.Control
                              as={FormSelect}
                              placeholder={`Select ${field.label}`}
                              options={field.options}
                              name={field.name}
                              value={formData[field.name]}
                              onChange={handleChange}
                              required={isSubmitButtonClicked}
                              disabled={field.disabled}
                              style={{
                                borderColor: "#224D99",
                                backgroundColor: field.disabled ? "#f8f9fa" : "white",
                                borderRadius: "8px",
                                padding: isMobile ? "10px" : "12px",
                              }}
                            />
                            {isSubmitButtonClicked && (
                              <Form.Control.Feedback type="invalid">
                                Please select {field.label}
                              </Form.Control.Feedback>
                            )}
                          </>
                        ) : field.type === "multiselect" ? (


      <Multiselect
        options={filteredFeatures}
        selectedValues={selectedFeatures}
        onSelect={(selectedList, selectedItem) => setSelectedFeatures(selectedList)}
        onRemove={(selectedList, removedItem) => setSelectedFeatures(selectedList)}
        displayValue="label"
        placeholder={formData?.roleId ? "Search features" : "Select a role first"}
        disable={!formData?.roleId}
        showCheckbox={true}
         closeOnSelect={false}
      style={{
          option: {
            backgroundColor: '#f5f7fa', // very light gray blue
            color: '#2c3e50',           // dark slate blue
          },
          selectedOption: {
            backgroundColor: '#7fb3d5', // soft blue
            color: 'white',
          },
          chips: {
            backgroundColor: '#85cbfdff', // light blue-gray
            color: 'black',           // medium blue text
            fontWeight: '500'
          },
          searchBox: {
            border: '1px solid #ccd6e0',
            borderRadius: '6px',
            padding: '8px',
          },
          optionContainer: {
            maxHeight: '180px',
            overflowY: 'auto',
          },
        }}
      />
                        ) : (
                          <>
                            <Form.Control
                              type={field.type}
                              placeholder={`Enter ${field.label}`}
                              name={field.name}
                              value={formData[field.name]}
                              onChange={handleChange}
                              required={
                                isSubmitButtonClicked && field.name !== "phoneNumber"
                              }
                              disabled={field.disabled}
                              style={{
                                borderColor: "#224D99",
                                backgroundColor: field.disabled ? "#f8f9fa" : "white",
                                borderRadius: "8px",
                                padding: isMobile ? "10px" : "12px",
                              }}
                            />
                            {isSubmitButtonClicked && (
                              <Form.Control.Feedback type="invalid">
                                Please enter a valid {field.label}
                              </Form.Control.Feedback>
                            )}
                          </>
                        )}
                      </Form.Group>
                    ))}

                    {!validated && (
                      <Col xs={12}>
                        <div className="alert alert-danger">
                          All required fields must be filled
                        </div>
                      </Col>
                    )}

                    {error && (
                      <Col xs={12}>
                        <div className="alert alert-danger">{error}</div>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
              <Modal.Footer className="border-0 pt-2 d-flex gap-2">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={submitLoader}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={submitLoader}
                  onClick={() => setIsSubmitButtonClicked(true)}
                >
                  {submitLoader ? (
                    <>
                      <CircularProgress size={16} color="inherit" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    "Save User"
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          </>
        ) : (
          <BulkUserUpload
            fetchUsers={fetchUsers}
            departments={departments}
            features={features}
            setSelectedFeatures={setSelectedFeatures}
            selectedFeatures={selectedFeatures}
            organizationDetails={organizationDetails}
             handleClose={handleClose} 
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;